import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Popconfirm, message, Modal, Form, InputNumber, Input, Select, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getFilterPriceList,
  saveFilterPrice,
  deleteFilterPrice,
  FilterPriceItem,
  FilterPriceSaveParams,
} from '@/api/customer';
import dayjs from 'dayjs';

const FilterPricePage: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<FilterPriceItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<FilterPriceItem | null>(null);
  const [form] = Form.useForm();

  // 加载数据
  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await getFilterPriceList();
      if (resp.success) {
        setDataSource(resp.result || []);
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadData();
  }, []);

  // 新增
  const handleAdd = () => {
    setEditRecord(null);
    form.resetFields();
    form.setFieldsValue({
      filterCode: '',
      filterName: '',
      monthlyFee: undefined,
      validDays: 0,
      unit: '次',
      status: 1,
      sortOrder: 0,
      remark: '默认价格：10元/次',
    });
    setModalOpen(true);
  };

  // 编辑
  const handleEdit = (record: FilterPriceItem) => {
    setEditRecord(record);
    form.setFieldsValue({
      id: record.id,
      filterCode: record.filterCode,
      filterName: record.filterName,
      monthlyFee: record.monthlyFee,
      validDays: record.validDays,
      unit: record.unit || '月',
      status: record.status,
      sortOrder: record.sortOrder,
      remark: record.remark,
    });
    setModalOpen(true);
  };

  // 删除
  const handleDelete = async (id: string) => {
    const resp = await deleteFilterPrice(id);
    if (resp.success) {
      messageApi.success('删除成功');
      loadData();
    } else {
      messageApi.error(resp.errorMsg || '删除失败');
    }
  };

  // 提交保存
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const params: FilterPriceSaveParams = {
        ...values,
        id: editRecord?.id,
      };
      const resp = await saveFilterPrice(params);
      if (resp.success) {
        messageApi.success(editRecord ? '修改成功' : '添加成功');
        setModalOpen(false);
        loadData();
      } else {
        messageApi.error(resp.errorMsg || '保存失败');
      }
    } catch (err) {
      console.log('表单校验失败:', err);
    }
  };

  return (
    <>
      {contextHolder}
      <Card className="m20" size="small">
        <Space className="mb15">
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加价格
          </Button>
          <Button onClick={loadData}>刷新</Button>
        </Space>

        <Table
          loading={loading}
          dataSource={dataSource}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            {
              title: '滤芯编码',
              dataIndex: 'filterCode',
              width: 120,
              render: (v) => <Tag color="blue">{v}</Tag>,
            },
            {
              title: '滤芯名称',
              dataIndex: 'filterName',
              width: 160,
            },
            {
              title: '单价（元）',
              dataIndex: 'monthlyFee',
              width: 110,
              render: (v) => <span style={{ fontWeight: 'bold', color: '#f5222d' }}>¥{v}</span>,
            },
            {
              title: '有效期',
              dataIndex: 'validDays',
              width: 100,
              render: (v, record) => {
                if (record.unit === '次') return '按次扣费';
                if (v > 0) return `${v}天`;
                return record.unit ? `${record.unit}费` : '-';
              },
            },
            {
              title: '计费单位',
              dataIndex: 'unit',
              width: 80,
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 80,
              render: (v) =>
                v === 1 ? (
                  <Tag color="green">启用</Tag>
                ) : (
                  <Tag color="default">停用</Tag>
                ),
            },
            {
              title: '排序',
              dataIndex: 'sortOrder',
              width: 60,
            },
            {
              title: '备注',
              dataIndex: 'remark',
              ellipsis: true,
            },
            {
              title: '操作',
              key: 'action',
              width: 140,
              fixed: 'right' as const,
              render: (_, record) => (
                <Space size="small">
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title={`确定删除「${record.filterName}」的价格配置？`}
                    onConfirm={() => handleDelete(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editRecord ? '编辑滤芯价格' : '添加滤芯价格'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        destroyOnHidden
        onOk={handleSubmit}
        okText="保存"
        cancelText="取消"
        width={520}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            unit: '次',
            status: 1,
            sortOrder: 0,
            validDays: 0,
          }}
        >
          <Form.Item
            label="滤芯编码"
            name="filterCode"
            rules={[{ required: true, message: '请输入滤芯编码' }]}
            extra="唯一标识，如 UF、CM、PP 等，不可重复"
          >
            <Input placeholder="如：UF、CM、PP" disabled={!!editRecord} />
          </Form.Item>

          <Form.Item
            label="滤芯名称"
            name="filterName"
            rules={[{ required: true, message: '请输入滤芯名称' }]}
          >
            <Input placeholder="如：UF超滤膜、CM后置压缩碳" />
          </Form.Item>

          <Space style={{ display: 'flex', width: '100%' }} size="middle">
            <Form.Item
              label="单价（元）"
              name="monthlyFee"
              rules={[{ required: true, message: '请输入单价金额' }, { type: 'number', min: 0.01, message: '金额必须大于0' }]}
              style={{ flex: 1 }}
            >
              <InputNumber
                placeholder="如：10"
                min={0}
                step={1}
                precision={2}
                prefix="¥"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              label="计费单位"
              name="unit"
              style={{ flex: 1 }}
            >
              <Select options={[
                { value: '月', label: '按月' },
                { value: '天', label: '按天' },
                { value: '次', label: '按次' },
              ]} />
            </Form.Item>
          </Space>

          <Space style={{ display: 'flex', width: '100%' }} size="middle">
            <Form.Item
              label="有效期天数"
              name="validDays"
              tooltip="滤芯的有效期天数，0表示不限制"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} placeholder="如：90、180、365，0=不限制" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="排序"
              name="sortOrder"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item
            label="状态"
            name="status"
            valuePropName="checked"
            getValueFromEvent={(e) => (e ? 1 : 0)}
          >
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item
            label="备注说明"
            name="remark"
          >
            <Input.TextArea rows={2} placeholder="可选，填写补充说明" maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default FilterPricePage;
